import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type ModalType =
  | 'chatBox'
  | 'feedback'
  | 'phoneWalkthrough'
  | 'chromeExtensionWalkthrough'
  | 'productWalkthrough'
  | 'linkedInImport'
  | 'outlookEmail'
  | 'notionImport'
  | null;

export interface ModalProps {
  // ChatBox props
  userId?: string;
  context?: {
    currentRoute?: string;
    selectedFounders?: string[];
    activeTab?: { id: string; name: string } | null;
  };
  onRefreshFounders?: () => Promise<void>;
  // OutlookEmail props
  connectedEmail?: string;
  // NotionImport props
  initialStep?: 'connect' | 'mapping';
  // Completion handlers
  onComplete?: (skipped?: boolean) => void;
  // User update handler (for imports that update user data)
  onUserUpdate?: (user: any) => void;
}

interface ModalState {
  activeModal: ModalType;
  modalProps: ModalProps;
  // Walkthrough sequence state
  walkthroughFromOnboarding: boolean;
  onboardingWalkthroughStep: number;
}

interface ModalContextValue extends ModalState {
  openModal: (type: ModalType, props?: ModalProps) => void;
  closeModal: () => void;
  isModalOpen: (type: ModalType) => boolean;
  setWalkthroughSequence: (fromOnboarding: boolean, step: number) => void;
}

const ModalContext = createContext<ModalContextValue | undefined>(undefined);

export const useModal = (): ModalContextValue => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

interface ModalProviderProps {
  children: ReactNode;
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [state, setState] = useState<ModalState>({
    activeModal: null,
    modalProps: {},
    walkthroughFromOnboarding: false,
    onboardingWalkthroughStep: 0,
  });

  const openModal = useCallback((type: ModalType, props: ModalProps = {}) => {
    setState(prev => ({
      ...prev,
      activeModal: type,
      modalProps: props,
    }));
  }, []);

  const closeModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      activeModal: null,
      modalProps: {},
    }));
  }, []);

  const isModalOpen = useCallback(
    (type: ModalType) => state.activeModal === type,
    [state.activeModal]
  );

  const setWalkthroughSequence = useCallback((fromOnboarding: boolean, step: number) => {
    setState(prev => ({
      ...prev,
      walkthroughFromOnboarding: fromOnboarding,
      onboardingWalkthroughStep: step,
    }));
  }, []);

  return (
    <ModalContext.Provider
      value={{
        ...state,
        openModal,
        closeModal,
        isModalOpen,
        setWalkthroughSequence,
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};

export default ModalContext;
